import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PolicyAcceptanceModal from './PolicyAcceptanceModal';
import { normalizeRole, needsPolicyAcceptance } from '../utils/roles';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, isLoading, checkPolicies, user } = useAuth();
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [checkingPolicies, setCheckingPolicies] = useState(true);

  useEffect(() => {
    const verifyPolicies = async () => {
      if (isAuthenticated && user) {
        // Employees and content uploaders: policy check; admins/co-admins bypass
        if (needsPolicyAcceptance(user.role)) {
          const hasAccepted = await checkPolicies();
          setShowPolicyModal(!hasAccepted);
        }
      }
      setCheckingPolicies(false);
    };

    if (!isLoading && isAuthenticated) {
      verifyPolicies();
    } else if (!isLoading) {
      setCheckingPolicies(false);
    }
  }, [isAuthenticated, isLoading, user, checkPolicies]);

  if (isLoading || checkingPolicies) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (
    allowedRoles &&
    user &&
    !allowedRoles.map(normalizeRole).includes(normalizeRole(user.role))
  ) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Show policy modal if user hasn't accepted required policies
  if (showPolicyModal && user && needsPolicyAcceptance(user.role)) {
    return (
      <>
        <PolicyAcceptanceModal
          onAccept={async () => {
            await checkPolicies();
            setShowPolicyModal(false);
          }}
        />
        {/* Block access to children while modal is shown */}
        <div style={{ display: 'none' }}>{children}</div>
      </>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
